export type StepState = "complete" | "current" | "upcoming";

export interface IStep {
  id: string | number;
  label: string;
}

export interface StepperProps {
  steps: IStep[];
  currentStep: number;
  onStepChange?: (index: number) => void;
  className?: string;
}

export default function Stepper({
  steps,
  currentStep,
  onStepChange,
  className = "",
}: StepperProps) {
  const getState = (index: number): StepState => {
    if (index < currentStep) return "complete";
    if (index === currentStep) return "current";
    return "upcoming";
  };

  return (
    <nav
      aria-label="Progress"
      className={`bg-white p-4 rounded-2xl shadow-sm w-full ${className}`}
    >
      <ol className="flex items-center justify-between w-full relative">
        {steps.map((step, idx) => {
          const state = getState(idx);
          const isLast = idx === steps.length - 1;

          return (
            <li
              key={step.id}
              className="flex-1 flex flex-col items-center text-center relative"
            >
              {/* Connector line */}
              {!isLast && (
                <div
                  className={`absolute top-5 left-1/2 w-full h-[2px] -translate-x-0.5 ${
                    state === "complete" ? "bg-gray-700" : "bg-gray-200"
                  }`}
                  style={{ zIndex: 0 }}
                />
              )}

              {/* Step circle */}
              <button
                type="button"
                onClick={() => onStepChange?.(idx)}
                aria-current={state === "current" ? "step" : undefined}
                className={`relative z-10 flex items-center justify-center rounded-full border transition-all duration-200 focus:outline-none
                  ${
                    state === "complete"
                      ? "bg-gray-700 border-gray-700 text-white"
                      : state === "current"
                      ? "border-gray-700 ring-2 ring-gray-300"
                      : "border-gray-300 bg-white text-gray-400"
                  }
                  w-10 h-10`}
              >
                {state === "complete" ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : state === "current" ? (
                  <div className="w-3 h-3 bg-gray-700 rounded-full" />
                ) : (
                  <span className="text-sm">{idx + 1}</span>
                )}
              </button>

              {/* Label */}
              <p
                className={`mt-3 text-sm ${
                  state === "complete"
                    ? "text-gray-700"
                    : state === "current"
                    ? "text-gray-800 font-semibold"
                    : "text-gray-400"
                }`}
              >
                {step.label}
              </p>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
